import React, { useState, useEffect } from "react";
import axios from "axios";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import { API_calculate_metric, API_get_saved_reports, API_get_company_metrics, API_delete_saved_report, API_update_saved_reports_order, API_get_event_metrics, API_get_report_tabs, API_create_report_tab, API_calculate_employee_metric, API_get_employee_metrics, API_get_store_team_members, API_save_report } from "../../../constants";
import TemplatePickerModal from "./templates/TemplatePickerModal";
import TopBarDisplayTitleNoLocationSelect from "../home/top_bar_components/TopBarDisplayTitleNoLocationSelect";
import WidgetRenderer from "./charts/WidgetRenderer";
import { formatDate } from "../requests/utilities/helpers";
import LoadSpinner from "../../../utilities/LoadSpinner";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SavedReportsModal from "./SavedReportsModal";
import SavedReportsView from "./SavedReportsView";
import LogbookReportCard from "./LogbookReportCard";
import Button from "@mui/material/Button";
import SettingsIcon from "@mui/icons-material/Settings";
import { toast } from "react-toastify";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import UndoIcon from "@mui/icons-material/Undo";
import Tooltip from "@mui/material/Tooltip";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  getAllDateRangeOptions,
  getBusinessWeekStartDayIndex,
  getDateRangeDisplayLabel,
  getPresetDateRange,
  DATE_RANGE_GROUPS,
} from "./dateRangePresets";


const availableStaticMetrics = [
  {
    value: "forecasted_sales",
    label: "Forecasted Sales",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "forecasted_adjusted_sales",
    label: "Forecasted Adjusted Sales",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "actual_sales",
    label: "Actual Sales",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "actual_labor_cost",
    label: "Actual Labor Cost",
    format: "dollars",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_sales_minus_labor_cost",
    label: "Actual Sales - Labor Cost",
    format: "dollars",
    Daily: true,
    Intraday: false,
  },
  {
    value: "splh",
    label: "Actual SPLH",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "forecasted_splh",
    label: "Forecasted SPLH",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "scheduled_splh",
    label: "Scheduled SPLH",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "earned_splh",
    label: "Earned SPLH",
    format: "dollars",
    Daily: true,
    Intraday: true,
  },
  {
    value: "scheduled_labor_hours",
    label: "Scheduled Hours",
    format: "hours",
    Daily: true,
    Intraday: true,
  },
  {
    value: "forecasted_labor_hours",
    label: "Forecasted Hours",
    format: "hours",
    Daily: true,
    Intraday: true,
  },
  {
    value: "actual_labor_hours",
    label: "Actual Hours",
    format: "hours",
    Daily: true,
    Intraday: true,
  },
  {
    value: "earned_labor_hours",
    label: "Earned Hours",
    format: "hours",
    Daily: true,
    Intraday: true,
  },
  {
    value: "logbook_entries",
    label: "Logbook Entries",
    format: "number",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_to_forecasted_sales",
    label: "Actual to Forecasted Sales",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_to_forecasted_adjusted_sales",
    label: "Actual to Forecasted Adjusted Sales",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "scheduled_to_forecasted_hours",
    label: "Scheduled to Forecasted Hours",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_to_earned_hours",
    label: "Actual to Earned Hours",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "actual_to_scheduled_hours",
    label: "Actual to Scheduled Hours",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
  {
    value: "earned_to_forecasted_hours",
    label: "Earned to Forecasted Hours",
    format: "percentage",
    Daily: true,
    Intraday: false,
  },
];

const laborPercentMetric = {
  value: "labor_percent",
  label: "Labor Percent",
  format: "percentage",
  Daily: true,
  Intraday: false,
};

const getLaborPercentEnabled = (storeNames, storeId) => {
  try {
    return Boolean(
      storeNames.find((store) => String(store.id) === String(storeId))
        ?.labor_percent_enabled
    );
  } catch (error) {
    return false;
  }
};

const getAvailableStaticMetrics = (laborPercentEnabled) => {
  if (!laborPercentEnabled) {
    return availableStaticMetrics;
  }

  return [...availableStaticMetrics, laborPercentMetric];
};


// Helper function to calculate dynamic date range
function getDynamicDateRange(originalDateRange, businessWeekStartDayIndex) {
  const presetRange = getPresetDateRange({
    preset: originalDateRange,
    businessWeekStartDayIndex,
  });

  if (presetRange?.requiresCustomInput) {
    return null;
  }

  if (presetRange?.startDate && presetRange?.endDate) {
    return {
      start: new Date(presetRange.startDate),
      end: new Date(presetRange.endDate),
    };
  }

  const fallbackRange = getPresetDateRange({
    preset: "last_week",
    businessWeekStartDayIndex,
  });

  if (fallbackRange?.startDate && fallbackRange?.endDate) {
    console.warn(
      `Unknown date range: ${originalDateRange}, using last completed week as fallback`
    );
    return {
      start: new Date(fallbackRange.startDate),
      end: new Date(fallbackRange.endDate),
    };
  }

  return null;
}

// Helper function to format date range for display
function formatDateRangeForDisplay(dateRangeString, customStartDate, customEndDate) {
  if (dateRangeString === "custom") {
    if (customStartDate && customEndDate) {
      return `${customStartDate} to ${customEndDate}`;
    }
    return "Custom Range";
  }

  return getDateRangeDisplayLabel(dateRangeString);
}



export function ReportCard({
  report,
  storeNames,
  availableMetrics,
  onRefresh,
  isLoading,
  dateRangeOverride,
  overrideStartDate,
  overrideEndDate,
  businessWeekStartDayIndex,
  homeStore,
}) {
  const [chartData, setChartData] = useState([]);
  const [intradayData, setIntradayData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);

  // Default to expanded unless this report has a saved preference.
  const [showSummaryCards, setShowSummaryCards] = useState(() => {
    if (!report?.id) return true;
    const saved = localStorage.getItem(`dashboard_summary_cards_${report.id}`);
    return saved === null || saved === "true";
  });

  const axiosConfig = getDefaultAxiosConfig();

  // Track the current report.id to detect switches
  const prevReportIdRef = React.useRef(report?.id);

  // Reload showSummaryCards when report.id changes
  useEffect(() => {
    if (!report?.id) return;
    if (prevReportIdRef.current !== report.id) {
      const saved = localStorage.getItem(`dashboard_summary_cards_${report.id}`);
      setShowSummaryCards(saved === null || saved === "true");
      prevReportIdRef.current = report.id;
    }
  }, [report?.id]);

  // Save showSummaryCards preference to localStorage when user toggles
  useEffect(() => {
    if (!report?.id) return;
    localStorage.setItem(`dashboard_summary_cards_${report.id}`, showSummaryCards.toString());
  }, [showSummaryCards]);

  useEffect(() => {
    fetchReportData();
  }, [report, dateRangeOverride, overrideStartDate, overrideEndDate]);

  const fetchReportData = async () => {
    setLoadingChart(true);

    try {
      // Debug logging
      console.log('Report data:', report);
      console.log('Date range:', report.selected_date_range);
      console.log('Custom start:', report.custom_start_date);
      console.log('Custom end:', report.custom_end_date);

      // Check if there's a global date range override
      let startDate, endDate;
      if (dateRangeOverride) {
        // Use the override date range
        if (dateRangeOverride === "custom") {
          console.log('Using override custom dates:', overrideStartDate, 'to', overrideEndDate);
          startDate = formatDate(overrideStartDate);
          endDate = formatDate(overrideEndDate);
        } else {
          console.log('Using override dynamic date range for:', dateRangeOverride);
          const dateRange = getDynamicDateRange(
            dateRangeOverride,
            businessWeekStartDayIndex
          );
          if (!dateRange) {
            console.warn('Unable to resolve override date range, aborting fetch');
            setLoadingChart(false);
            return;
          }
          startDate = formatDate(dateRange.start);
          endDate = formatDate(dateRange.end);
        }
      } else {
        // Use report's saved date range (original behavior)
        if (report.selected_date_range === "custom" && report.custom_start_date && report.custom_end_date) {
          console.log('Using custom dates:', report.custom_start_date, 'to', report.custom_end_date);
          startDate = report.custom_start_date;
          endDate = report.custom_end_date;
        } else {
          console.log('Using dynamic date range for:', report.selected_date_range);
          const dateRange = getDynamicDateRange(
            report.selected_date_range,
            businessWeekStartDayIndex
          );
          if (!dateRange) {
            console.warn('Unable to resolve saved date range, aborting fetch');
            setLoadingChart(false);
            return;
          }
          startDate = formatDate(dateRange.start);
          endDate = formatDate(dateRange.end);
        }
      }
      console.log('Final dates being sent to API:', startDate, 'to', endDate);

      // Validate required arrays before using them
      const isEmployeeReport = report.report_type === "employee";

      if (isEmployeeReport) {
        if (!Array.isArray(report.selected_employees) || report.selected_employees.length === 0) {
          console.error('Invalid selected_employees array for employee report');
          setLoadingChart(false);
          return;
        }
      } else {
        if (!Array.isArray(report.selected_stores) || report.selected_stores.length === 0) {
          console.error('Invalid selected_stores array');
          setLoadingChart(false);
          return;
        }
      }

      if (!Array.isArray(report.selected_metrics) || report.selected_metrics.length === 0) {
        console.error('Invalid selected_metrics array');
        setLoadingChart(false);
        return;
      }

      let params;
      let url;

      if (isEmployeeReport) {
        // Employee report - use employee metrics API
        // Convert composite keys from empId-storeId to empId:storeId format for backend
        const convertedEmployees = report.selected_employees.map((key) => {
          const keyStr = String(key);
          if (keyStr.includes("-")) {
            const [empId, storeId] = keyStr.split("-");
            return `${empId}:${storeId}`;
          }
          // Legacy format without store ID
          return keyStr;
        });

        // Extract store_id from first employee's composite key, or use selected_stores
        let storeId = report.selected_stores?.[0];
        if (!storeId && report.selected_employees.length > 0) {
          const firstKey = String(report.selected_employees[0]);
          if (firstKey.includes("-")) {
            storeId = firstKey.split("-")[1];
          }
        }

        params = {
          selectedEmployees: convertedEmployees.join(","),
          selectedMetrics: report.selected_metrics.join(","),
          selectedStartDate: startDate,
          selectedEndDate: endDate,
          store_id: storeId || homeStore,
          activeTab: report.active_tab || "Daily",
          selectedRadioOption: report.selected_radio_option || "Default",
        };
        url = API_calculate_employee_metric;
        console.log("Dashboard fetch - employee report using API_calculate_employee_metric");
      } else {
        // Location report - use standard metrics API
        params = {
          selectedStores: report.selected_stores.join(","),
          selectedMetrics: report.selected_metrics.join(","),
          selectedStartDate: startDate,
          selectedEndDate: endDate,
          store_id: report.selected_stores[0],
          comparingMetricsFlag: false,
          activeTab: report.active_tab || "Daily",
          selectedRadioOption: report.selected_radio_option || "Default",
        };

        // Check if all selected metrics are analytics metrics (same logic as ReportingDisplay)
        const metricObjs = report.selected_metrics.map((m) =>
          availableMetrics.find((am) => am.value === m)
        );
        const allAnalytics = metricObjs.every((m) => m && m.analytics);
        url = allAnalytics ? API_get_event_metrics : API_calculate_metric;

        console.log("Dashboard fetch - metrics:", report.selected_metrics);
        console.log("Dashboard fetch - metricObjs:", metricObjs);
        console.log("Dashboard fetch - allAnalytics:", allAnalytics);
        console.log("Dashboard fetch - using URL:", url === API_get_event_metrics ? "API_get_event_metrics" : "API_calculate_metric");
      }

      const response = await axios.get(url, { params, ...axiosConfig });
      const responseData = response.data.redux.payload;

      // Extract all the data needed for charts and summary cards (same as ReportingDisplay)
      const newData = responseData.agg_data?.sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      ) || [];
      const newIntradayData = responseData.intraday_data?.sort(
        (a, b) => a.qh_num - b.qh_num
      ) || [];
      const newRawData = responseData.raw_data?.sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      ) || [];
      const newSummaryData = responseData.summary_data || [];
      // For employee reports, use employee_data or role_data depending on comparison mode
      // For location reports, use location_data
      let newLocationData;
      if (isEmployeeReport) {
        if (report.selected_radio_option === "Compare by Role") {
          newLocationData = responseData.role_data || [];
        } else {
          newLocationData = responseData.employee_data || [];
        }
      } else {
        newLocationData = responseData.location_data || [];
      }

      setChartData(newData);
      setIntradayData(newIntradayData);
      setRawData(newRawData);
      setSummaryData(newSummaryData);
      setLocationData(newLocationData);
    } catch (error) {
      console.error("Error fetching report data:", error);
      setChartData([]);
      setIntradayData([]);
      setRawData([]);
      setSummaryData([]);
      setLocationData([]);
    } finally {
      setLoadingChart(false);
    }
  };

  // Get metric information (same logic as ReportingDisplay)
  const getMetricInfo = (metricValue) => {
    if (!metricValue) {
      return { label: "Unknown Metric", format: "number" };
    }

    // If availableMetrics is not loaded yet, return the raw value as fallback
    if (!availableMetrics || !Array.isArray(availableMetrics) || availableMetrics.length === 0) {
      return { label: metricValue, format: "number" };
    }

    let baseValue = metricValue;
    let suffix = "";

    if (metricValue.endsWith("_previous_year")) {
      baseValue = metricValue.replace("_previous_year", "");
      suffix = " (Prev Year)";
    }

    const metric = availableMetrics.find((m) => m.value === baseValue);

    if (metric) {
      return {
        label: `${metric.label}${suffix}`,
        format: metric.format,
      };
    }

    // Fallback to the raw value if metric not found
    return { label: metricValue, format: "number" };
  };

  // Get store names or employee names
  const getStoreNames = () => {
    // For employee reports, return employee names
    if (report.report_type === "employee") {
      // Use stored names if available
      if (report.selected_employee_names && report.selected_employee_names.length > 0) {
        return report.selected_employee_names.join(", ");
      }
      // Fall back to count for older saved reports
      const employeeCount = report.selected_employees?.length || 0;
      return `${employeeCount} employee${employeeCount !== 1 ? 's' : ''}`;
    }

    // For location reports, return store names
    if (!storeNames || !Array.isArray(storeNames) || !report.selected_stores || !Array.isArray(report.selected_stores)) {
      return "No stores selected";
    }
    const selectedSet = new Set(report.selected_stores.map(String));
    return storeNames
      .filter(store => store && selectedSet.has(String(store.id)))
      .map(store => store.store_name || 'Unknown Store')
      .join(", ") || "No stores selected";
  };

  // Expand metrics for previous-year comparisons, matching ReportingDisplay behavior.
  const displayMetrics = React.useMemo(() => {
    const baseMetrics = Array.isArray(report.selected_metrics)
      ? [...report.selected_metrics]
      : [];

    if (report.selected_radio_option === "Compare to Previous Year") {
      baseMetrics.forEach((metric) => {
        if (!metric.endsWith("_previous_year")) {
          baseMetrics.push(`${metric}_previous_year`);
        }
      });
    }

    return baseMetrics;
  }, [report.selected_metrics, report.selected_radio_option]);

  // Calculate comparison modes (same logic as ReportingDisplay)
  // For employee reports, "Compare by Employee" and "Compare by Role" need pivot-by-entity behavior
  const compareLocationsChecked = report.selected_radio_option === "Compare by Store" ||
    report.selected_radio_option === "Compare by Employee" ||
    report.selected_radio_option === "Compare by Role";
  const compareDow = report.selected_radio_option === "Compare by Day Of Week";
  const compareMetricsChecked = report.selected_radio_option === "Compare by Metric" ||
    report.selected_radio_option === "Compare to Previous Year";

  // Get display label for comparison mode
  const getComparisonLabel = () => {
    switch (report.selected_radio_option) {
      case "Compare by Store": return "Comparing Stores";
      case "Compare by Employee": return "Comparing Employees";
      case "Compare by Role": return "Comparing Roles";
      case "Compare by Metric": return "Comparing Metrics";
      case "Compare to Previous Year": return "Comparing Previous Year";
      default: return "Comparing";
    }
  };

  const primaryMetric = displayMetrics?.[0];
  const metricInfo = getMetricInfo(primaryMetric);

  // Enrich summary and location data with labels and formats (same logic as ReportingDisplay)
  // Add defensive checks to prevent white screen errors
  const enrichedSummaryData = (Array.isArray(summaryData) ? summaryData : [])
    .filter((data) => data && data.metric) // Filter out invalid data
    .map((data) => {
      const metricInfo = getMetricInfo(data.metric);
      return {
        ...data,
        metric: data.metric || '', // Ensure metric is always a string
        label: metricInfo.label,
        format: metricInfo.format,
      };
    });

  const enrichedLocationData = (Array.isArray(locationData) ? locationData : [])
    .filter((data) => data && data.metric) // Filter out invalid data
    .map((data) => {
      const metricInfo = getMetricInfo(data.metric);
      return {
        ...data,
        metric: data.metric || '', // Ensure metric is always a string
        label: metricInfo.label,
        format: metricInfo.format,
        // Compute display name for location/employee/role cards
        displayName: data.location || data.employee || data.role || 'Unknown',
      };
    });

  // Don't render if report data is incomplete
  if (!report || !report.name || !report.selected_metrics || report.selected_metrics.length === 0) {
    return (
      <div className="h-full bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <p className="text-red-600 text-sm">
            Invalid report data
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
      <div className="p-6 flex-1 flex flex-col">
        <div className="mb-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900 leading-tight">
              {report.name}
            </h3>
            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
              {/* Summary Cards Toggle */}
              {!loadingChart && (enrichedSummaryData.length > 0 || enrichedLocationData.length > 0) && (
                <button
                  onClick={() => setShowSummaryCards(!showSummaryCards)}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-violet-600 hover:bg-violet-50 transition-colors duration-200"
                  title={showSummaryCards ? "Hide summary cards" : "Show summary cards"}
                >
                  <span>Summary</span>
                  {showSummaryCards ? (
                    <ExpandLessIcon style={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreIcon style={{ fontSize: 16 }} />
                  )}
                </button>
              )}
              <div className="text-xs text-right">
                {dateRangeOverride ? (
                  <div>
                    <span className="text-violet-600 font-medium">
                      {dateRangeOverride === "custom"
                        ? `${formatDate(overrideStartDate)} to ${formatDate(overrideEndDate)}`
                        : formatDateRangeForDisplay(dateRangeOverride, null, null)}
                    </span>
                    <span className="ml-1 text-xxs text-gray-500">(Override)</span>
                  </div>
                ) : (
                  <span className="text-gray-500">
                    {formatDateRangeForDisplay(report.selected_date_range, report.custom_start_date, report.custom_end_date)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-sm text-gray-600">
              {getStoreNames()}
            </p>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>
                {displayMetrics?.length === 1
                  ? `Metric: ${metricInfo?.label || "Unknown"}`
                  : `Metrics: ${displayMetrics?.map(m => getMetricInfo(m).label).join(", ")}`}
              </span>

              <span className="text-violet-600 font-medium">
                {report.widget_type === "line" ? "Line Chart" :
                 report.widget_type === "bar" ? "Bar Chart" :
                 report.widget_type === "pie" ? "Pie Chart" :
                 report.widget_type === "gauge" ? "Gauge" :
                 report.widget_type === "table" ? "Table" :
                 report.widget_type === "cards" ? "Metric Cards" : "Line Chart"}
              </span>

              {report.report_type !== "logbook" && (
                <span className="text-blue-600 font-medium">
                  {(compareLocationsChecked || compareDow || compareMetricsChecked)
                    ? `${(compareLocationsChecked || compareMetricsChecked) ? getComparisonLabel() : "Comparing Days of Week"} • ${report.active_tab || "Daily"}`
                    : report.active_tab || "Daily"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards - same structure as ReportingDisplay.js:1404-1537 */}
        {!loadingChart && showSummaryCards && (enrichedSummaryData.length > 0 || enrichedLocationData.length > 0) && (
          <div className="mb-4">
            {!compareLocationsChecked ? (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {enrichedSummaryData.map((metric, index) => (
                  <div
                    key={index}
                    className="p-3 border border-slate-200 rounded-lg min-w-[140px] text-xs"
                  >
                    <p className="text-xs text-slate-900 font-medium">{metric.label}</p>
                    {metric.format === "dollars" ? (
                      <div className="mt-1.5 flex space-x-4">
                        {metric.metric && !metric.metric.includes("splh") && (
                            <div>
                              <p className="text-xxs text-slate-500">Daily Avg</p>
                              <p className="text-sm text-slate-900">
                                {(metric.average || 0).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                })}
                              </p>
                            </div>
                          )}
                        <div>
                          <p className="text-xxs text-slate-500">Total</p>
                          <p className="text-sm text-slate-900">
                            {(metric.total || 0).toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                          </p>
                        </div>
                      </div>
                    ) : metric.format === "hours" ||
                      metric.format === "units" ||
                      metric.format === "number" ? (
                      <div className="mt-1.5 flex space-x-4">
                        {metric.metric && metric.metric !== "entrees_plh" && (
                          <div>
                            <p className="text-xxs text-slate-500">Daily Avg</p>
                            <p className="text-sm text-slate-900">
                              {(metric.average || 0).toFixed(2)}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-xxs text-slate-500">Total</p>
                          <p className="text-sm text-slate-900">
                            {(metric.total || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ) : metric.format === "percentage" ? (
                      <div className="mt-1.5 flex space-x-4">
                        <div>
                          <p className="text-xxs text-slate-500">Total</p>
                          <p className="text-sm text-slate-900">
                            {((metric.total || 0) * 100).toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {enrichedLocationData.map((location, index) => (
                  <div
                    key={index}
                    className="p-3 border border-slate-200 rounded-lg min-w-[140px] text-xs"
                  >
                    <p className="text-xs text-slate-900 font-medium">{location.displayName}</p>
                    {location.format === "dollars" ? (
                      <div className="mt-1.5 flex space-x-4">
                        {location.metric && !location.metric.includes("splh") && (
                            <div>
                              <p className="text-xxs text-slate-500">Daily Avg</p>
                              <p className="text-sm text-slate-900">
                                {(location.average || 0).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                })}
                              </p>
                            </div>
                          )}
                        <div>
                          <p className="text-xxs text-slate-500">Total</p>
                          <p className="text-sm text-slate-900">
                            {(location.total || 0).toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                          </p>
                        </div>
                      </div>
                    ) : location.format === "hours" ||
                      location.format === "units" ||
                      location.format === "number" ? (
                      <div className="mt-1.5 flex space-x-4">
                        {location.metric && location.metric !== "entrees_plh" && (
                          <div>
                            <p className="text-xxs text-slate-500">Daily Avg</p>
                            <p className="text-sm text-slate-900">
                              {(location.average || 0).toFixed(2)}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-xxs text-slate-500">Total</p>
                          <p className="text-sm text-slate-900">
                            {(location.total || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ) : location.format === "percentage" ? (
                      <div className="mt-1.5 flex space-x-4">
                        <div>
                          <p className="text-xxs text-slate-500">Total</p>
                          <p className="text-sm text-slate-900">
                            {((location.total || 0) * 100).toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 min-h-0">
          {loadingChart ? (
            <div className="flex items-center justify-center h-full">
              <LoadSpinner />
            </div>
          ) : chartData.length > 0 || intradayData.length > 0 ? (
            <WidgetRenderer
              widgetType={report.widget_type || "line"}
              activeTab={report.active_tab || "Daily"}
              intradayData={intradayData || []}
              data={chartData || []}
              rawData={rawData || []}
              selectedMetrics={displayMetrics || []}
              metrics={availableMetrics || []}
              compareLocations={compareLocationsChecked}
              compareDow={compareDow}
              summaryData={enrichedSummaryData}
              locationData={enrichedLocationData}
              isDashboard={true}
              layout={report.chart_layout}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <p className="text-sm">No data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardDisplay(props) {
  const [savedReports, setSavedReports] = useState([]);
  const laborPercentEnabled = getLaborPercentEnabled(
    props.storeNames,
    props.homeStore
  );
  const staticMetrics = React.useMemo(
    () => getAvailableStaticMetrics(laborPercentEnabled),
    [laborPercentEnabled]
  );
  const [availableMetrics, setAvailableMetrics] = useState(staticMetrics);
  const [loading, setLoading] = useState(true);
  const [showSavedReportsDialog, setShowSavedReportsDialog] = useState(false);
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [showManageReports, setShowManageReports] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null); // null = "All Reports"

  // Date range override state
  const [dateRangeOverride, setDateRangeOverride] = useState(null); // null = no override
  const [overrideStartDate, setOverrideStartDate] = useState(null);
  const [overrideEndDate, setOverrideEndDate] = useState(null);
  const [showCustomDateOverride, setShowCustomDateOverride] = useState(false);

  // Pending custom dates (not applied until "Go" is clicked)
  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [pendingEndDate, setPendingEndDate] = useState(null);

  // Template picker state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  const axiosConfig = getDefaultAxiosConfig();
  const businessWeekStartDayIndex = getBusinessWeekStartDayIndex(
    props.storeNames,
    props.homeStore
  );

  useEffect(() => {
    Promise.all([fetchSavedReports(), fetchMetrics(), fetchTabs()])
      .finally(() => setLoading(false));
  }, [props.homeStore, staticMetrics]);

  // Refresh when the operations assistant saves a new report to the dashboard
  useEffect(() => {
    if (
      props.opsRefreshSignal &&
      props.opsRefreshSignal.types?.includes("saved_reports")
    ) {
      fetchSavedReports();
      fetchTabs();
    }
  }, [props.opsRefreshSignal]);

  const fetchSavedReports = async () => {
    try {
      const params = {
        store_id: props.homeStore  // Required for API authentication
      };

      const response = await axios.get(API_get_saved_reports, { params, ...axiosConfig });
      setSavedReports(response.data || []);
    } catch (error) {
      console.error("Error fetching saved reports:", error);
      setSavedReports([]);
    }
  };

  const fetchMetrics = async () => {
    try {
      const params = { store_id: props.homeStore };

      // Fetch both store metrics and employee metrics
      const [storeResponse, employeeResponse] = await Promise.all([
        axios.get(API_get_company_metrics, { params, ...axiosConfig }),
        axios.get(API_get_employee_metrics, { params, ...axiosConfig }),
      ]);

      // Employee metrics API wraps response in redux.payload
      const employeeMetrics = employeeResponse.data?.redux?.payload || employeeResponse.data || [];

      // Combine static metrics with dynamic store metrics and employee metrics
      const staticMetricValues = new Set(
        staticMetrics.map((metric) => metric.value)
      );
      const storeMetrics = (storeResponse.data || []).filter(
        (metric) => !staticMetricValues.has(metric.value)
      );
      setAvailableMetrics([
        ...staticMetrics,
        ...storeMetrics,
        ...employeeMetrics,
      ]);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      // Keep static metrics even if dynamic fetch fails
      setAvailableMetrics(staticMetrics);
    }
  };

  const fetchTabs = async () => {
    try {
      const params = { store_id: props.homeStore };
      const response = await axios.get(API_get_report_tabs, { params, ...axiosConfig });
      setTabs(response.data || []);
    } catch (error) {
      console.error("Error fetching tabs:", error);
      setTabs([]);
    }
  };

  const handleRefreshAll = () => {
    setLoading(true);
    fetchSavedReports().finally(() => setLoading(false));
  };

  const handleDeleteSavedReport = async (reportId) => {
    const params = {
      store_id: props.homeStore
    };

    // Show loading toast
    toast.loading("Deleting report...", { autoClose: false });

    try {
      await axios.delete(API_delete_saved_report + "/" + reportId, { params, ...axiosConfig });
      // Update the list by filtering out the deleted report - no loading indicator
      setSavedReports(prevReports => prevReports.filter(report => report.id !== reportId));
      // Refresh tabs to update the report counts
      await fetchTabs();

      // Dismiss loading toast and show success
      toast.dismiss();
      toast.success("Report deleted successfully!", { autoClose: 3000 });
    } catch (error) {
      console.error("Error deleting saved report:", error);
      // Dismiss loading toast and show error
      toast.dismiss();
      toast.error("Failed to delete report", { autoClose: 3000 });
    }
  };

  const handleReorderReports = (result) => {
    if (!result.destination) return;

    const reorderedReports = Array.from(savedReports);
    const [movedReport] = reorderedReports.splice(result.source.index, 1);
    reorderedReports.splice(result.destination.index, 0, movedReport);

    // Update local state immediately for better UX
    setSavedReports(reorderedReports);

    // Prepare the update payload
    const reportOrders = reorderedReports.map((report, index) => ({
      id: report.id,
      order: index
    }));

    // Send to backend
    const updateData = {
      store_id: props.homeStore,
      report_orders: reportOrders
    };

    axios
      .post(API_update_saved_reports_order, updateData, axiosConfig)
      .then((res) => {
        console.log('Report order updated successfully');
      })
      .catch((error) => {
        console.error('Error updating report order:', error);
        // Revert to original order on error
        fetchSavedReports();
      });
  };

  const handleOpenSavedReportsDialog = () => {
    setShowManageReports(true);
  };

  const handleGoBack = async () => {
    setShowManageReports(false);

    // Refresh data when coming back from manage reports
    await fetchSavedReports();

    // Fetch tabs and check if the currently selected tab still exists
    if (selectedTab !== null) {
      try {
        const params = { store_id: props.homeStore };
        const response = await axios.get(API_get_report_tabs, { params, ...axiosConfig });
        const updatedTabs = response.data || [];
        setTabs(updatedTabs);

        // Check if the currently selected tab still exists
        const tabStillExists = updatedTabs.some(tab => tab.id === selectedTab);
        if (!tabStillExists) {
          setSelectedTab(null);
        }
      } catch (error) {
        console.error("Error fetching tabs:", error);
        setTabs([]);
        setSelectedTab(null);
      }
    } else {
      // If no tab selected, just fetch tabs normally
      await fetchTabs();
    }
  };

  const handleApplyTemplate = async (template, storeIds, tabId) => {
    setApplyingTemplate(true);

    // Build a lookup for store names so per-store reports can include the name
    const storeNameLookup = {};
    if (Array.isArray(props.storeNames)) {
      props.storeNames.forEach((s) => {
        if (s && s.id != null) {
          storeNameLookup[String(s.id)] = s.store_name || `Store ${s.id}`;
        }
      });
    }

    try {
      const storeTabLookup = {};

      if (template.createStoreTabs) {
        const params = { store_id: props.homeStore };
        const existingTabsResponse = await axios.get(API_get_report_tabs, {
          params,
          ...axiosConfig,
        });
        const existingTabs = existingTabsResponse.data || [];
        const tabNameToId = new Map(existingTabs.map((t) => [t.name, t.id]));

        for (const storeId of storeIds) {
          const storeName = storeNameLookup[String(storeId)] || `Store ${storeId}`;
          let storeTabId = tabNameToId.get(storeName);

          if (!storeTabId) {
            try {
              const createResponse = await axios.post(
                API_create_report_tab,
                { name: storeName, store_id: props.homeStore },
                axiosConfig
              );
              storeTabId = createResponse.data?.id;
            } catch (createError) {
              // If tab creation races with another request, re-read tabs and continue.
              const refreshResponse = await axios.get(API_get_report_tabs, {
                params,
                ...axiosConfig,
              });
              const refreshedTab = (refreshResponse.data || []).find(
                (tab) => tab.name === storeName
              );
              storeTabId = refreshedTab?.id;
            }
          }

          if (storeTabId) {
            tabNameToId.set(storeName, storeTabId);
            storeTabLookup[String(storeId)] = storeTabId;
          }
        }
      }

      // Create each report sequentially to preserve display order
      for (const reportConfig of template.reports) {
        if (reportConfig.scope === "per_store") {
          // Create one report per selected store
          for (const storeId of storeIds) {
            const storeName = storeNameLookup[String(storeId)] || `Store ${storeId}`;
            const fullName = template.createStoreTabs
              ? reportConfig.name
              : `${reportConfig.name} — ${storeName}`;
            const reportData = {
              name: fullName.length > 255 ? fullName.slice(0, 255) : fullName,
              selected_stores: [storeId],
              selected_metrics: reportConfig.selected_metrics,
              selected_date_range: reportConfig.selected_date_range,
              active_tab: reportConfig.active_tab,
              selected_radio_option: reportConfig.selected_radio_option,
              widget_type: reportConfig.widget_type,
              tab_id: storeTabLookup[String(storeId)] || tabId || null,
              store_id: props.homeStore,
            };
            await axios.post(API_save_report, reportData, axiosConfig);
          }
        } else {
          // Aggregate: single report with all selected stores
          const reportData = {
            name: reportConfig.name,
            selected_stores: storeIds,
            selected_metrics: reportConfig.selected_metrics,
            selected_date_range: reportConfig.selected_date_range,
            active_tab: reportConfig.active_tab,
            selected_radio_option: reportConfig.selected_radio_option,
            widget_type: reportConfig.widget_type,
            tab_id: tabId || null,
            store_id: props.homeStore,
          };
          await axios.post(API_save_report, reportData, axiosConfig);
        }
      }

      setShowTemplateModal(false);
      toast.success(`Template "${template.name}" applied successfully!`, {
        autoClose: 3000,
      });

      // Refresh reports and tabs
      await Promise.all([fetchSavedReports(), fetchTabs()]);
    } catch (error) {
      console.error("Error applying template:", error);
      toast.error(
        "Failed to apply template. Some reports may have been created.",
        { autoClose: 5000 }
      );
      // Still refresh to show any reports that were created
      await Promise.all([fetchSavedReports(), fetchTabs()]);
    } finally {
      setApplyingTemplate(false);
    }
  };

  // Filter reports based on selected tab
  const getFilteredReports = () => {
    if (selectedTab === null) {
      // "All Reports" - show all
      return savedReports;
    }
    // Show only reports belonging to the selected tab
    return savedReports.filter((report) => report.tab_id === selectedTab);
  };

  const filteredReports = getFilteredReports();

  // Date range options with grouping
  const dateRangeMenuItems = React.useMemo(() => {
    const items = [
      <MenuItem
        key="default-header"
        value=""
        sx={{
          color: "#6d28d9",
          backgroundColor: dateRangeOverride === "" || dateRangeOverride === null
            ? "#f3e8ff"
            : "inherit",
          "&:hover": {
            backgroundColor: dateRangeOverride === "" || dateRangeOverride === null
              ? "#f3e8ff"
              : "#f9fafb",
          },
        }}
      >
        Default Ranges
      </MenuItem>
    ];

    DATE_RANGE_GROUPS.forEach((group) => {
      items.push(
        <MenuItem
          key={`${group.label}-header`}
          disabled
          sx={{ fontWeight: "bold", opacity: 1 }}
        >
          {group.label}
        </MenuItem>
      );

      group.options.forEach((option) => {
        items.push(
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              backgroundColor: dateRangeOverride === option.value
                ? "#f3e8ff"
                : "inherit",
            }}
          >
            {option.label}
          </MenuItem>
        );
      });
    });

    return items;
  }, [dateRangeOverride]);

  // Handle date range override change
  const handleDateRangeOverrideChange = (event) => {
    const value = event.target.value;

    if (value === "") {
      // Reset to default ranges
      setDateRangeOverride(null);
      setOverrideStartDate(null);
      setOverrideEndDate(null);
      setShowCustomDateOverride(false);
      setPendingStartDate(null);
      setPendingEndDate(null);
      return;
    }

    if (value === "custom") {
      // Show custom date pickers but DON'T set dateRangeOverride yet
      setShowCustomDateOverride(true);
      setPendingStartDate(null);
      setPendingEndDate(null);
      // Don't set dateRangeOverride - wait for Go button
      return;
    }

    // For non-custom ranges, set immediately
    setDateRangeOverride(value);
    setShowCustomDateOverride(false);

    const { startDate, endDate, requiresCustomInput } = getPresetDateRange({
      preset: value,
      businessWeekStartDayIndex,
    });

    if (requiresCustomInput || !startDate || !endDate) {
      setOverrideStartDate(null);
      setOverrideEndDate(null);
      return;
    }

    setOverrideStartDate(new Date(startDate));
    setOverrideEndDate(new Date(endDate));
  };

  // Reset date range override
  const handleResetDateRangeOverride = () => {
    setDateRangeOverride(null);
    setOverrideStartDate(null);
    setOverrideEndDate(null);
    setShowCustomDateOverride(false);
    setPendingStartDate(null);
    setPendingEndDate(null);
  };

  // Apply custom date range
  const handleApplyCustomDateRange = () => {
    if (pendingStartDate && pendingEndDate) {
      // Apply the pending dates to the actual override dates
      setOverrideStartDate(pendingStartDate);
      setOverrideEndDate(pendingEndDate);
      setDateRangeOverride("custom");
    }
  };

  // Show loading spinner while data is being fetched (matching Overview pattern)
  if (loading) {
    return (
      <div className="bg-white min-h-screen">
        <TopBarDisplayTitleNoLocationSelect title="Dashboard" />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      </div>
    );
  }

  // If showing manage reports, render the SavedReportsView
  if (showManageReports) {
    return (
      <SavedReportsView
        savedReports={savedReports}
        savedReportsLoading={savedReportsLoading}
        storeNames={props.storeNames}
        availableMetrics={availableMetrics}
        onDeleteReport={handleDeleteSavedReport}
        onReorderReports={handleReorderReports}
        onLoadReport={null} // No load functionality in dashboard
        showLoadButton={false} // Hide load button in dashboard
        title="Manage Dashboard Reports"
        description="Drag and drop to reorder reports in your dashboard. Delete reports you no longer need."
        onBack={handleGoBack}
        backButtonText="Back to Dashboard"
        homeStore={props.homeStore}
        onRefreshReports={fetchSavedReports}
        tabs={tabs}
        onRefreshTabs={fetchTabs}
      />
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div>
        <TopBarDisplayTitleNoLocationSelect title="Dashboard" />
      </div>

      {savedReports.length > 0 && (
        <div className="px-6 mt-6">
          <div className="flex items-center gap-4 flex-wrap">
            <Button
              variant="contained"
              onClick={handleRefreshAll}
              disabled={loading}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                height: "40px",
                backgroundColor: "#6d28d9",
                color: "white",
                "&:hover": {
                  backgroundColor: "#5b21b6",
                },
                "&:disabled": {
                  backgroundColor: "#d1d5db",
                  color: "#9ca3af",
                },
              }}
            >
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={handleOpenSavedReportsDialog}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                height: "40px",
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              Manage Reports
            </Button>

            <Button
              variant="outlined"
              onClick={() => setShowTemplateModal(true)}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                height: "40px",
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              Use Template
            </Button>

            {/* Date Range Override Controls */}
            <div className="flex items-center gap-2 ml-4">
              <CalendarTodayIcon className="text-gray-500" style={{ fontSize: 18 }} />
              <span className="text-sm text-gray-600">Date Range:</span>
            </div>

            {!showCustomDateOverride ? (
              <>
                <Select
                  value={dateRangeOverride || ""}
                  onChange={handleDateRangeOverrideChange}
                  displayEmpty
                  input={<OutlinedInput />}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    height: "40px",
                    fontSize: "14px",
                    minWidth: "200px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  }}
                >
                  {dateRangeMenuItems}
                </Select>

                {dateRangeOverride && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleResetDateRangeOverride}
                    sx={{
                      textTransform: "none",
                      borderRadius: "6px",
                      height: "40px",
                      borderColor: "#6d28d9",
                      color: "#6d28d9",
                      "&:hover": {
                        borderColor: "#5b21b6",
                        backgroundColor: "#f3f4f6",
                      },
                    }}
                  >
                    Reset
                  </Button>
                )}
              </>
            ) : (
              <>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    value={pendingStartDate}
                    onChange={(newValue) => setPendingStartDate(newValue)}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                      textField: {
                        placeholder: "Start Date",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        width: "160px",
                        "& fieldset": {
                          borderRadius: "8px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        display: "none",
                      },
                    }}
                  />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    value={pendingEndDate}
                    onChange={(newValue) => setPendingEndDate(newValue)}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                      textField: {
                        placeholder: "End Date",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        width: "160px",
                        "& fieldset": {
                          borderRadius: "8px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        display: "none",
                      },
                    }}
                  />
                </LocalizationProvider>

                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyCustomDateRange}
                  disabled={!pendingStartDate || !pendingEndDate}
                  sx={{
                    textTransform: "none",
                    borderRadius: "6px",
                    height: "40px",
                    backgroundColor: "#6d28d9",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                    "&:disabled": {
                      backgroundColor: "#d1d5db",
                      color: "#9ca3af",
                    },
                  }}
                >
                  Go
                </Button>

                <Tooltip title="Back to preset date ranges">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UndoIcon />}
                    onClick={handleResetDateRangeOverride}
                    sx={{
                      textTransform: "none",
                      borderRadius: "6px",
                      height: "40px",
                      borderColor: "#6d28d9",
                      color: "#6d28d9",
                      "&:hover": {
                        borderColor: "#5b21b6",
                        backgroundColor: "#f3f4f6",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Tooltip>
              </>
            )}
          </div>

          {/* Info message when override is active */}
          {dateRangeOverride && (
            <div className="mt-3 px-3 py-2 bg-violet-50 rounded-md border border-violet-200">
              <div className="text-xs text-violet-700 flex items-center gap-1">
                <InfoOutlinedIcon style={{ fontSize: 16 }} />
                <span>All reports below are using this date range override</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      {tabs.length > 0 && savedReports.length > 0 && (
        <div className="px-6 mt-4">
          <div className="flex items-center gap-2 border-b border-gray-200">
            {/* All Reports Tab */}
            <button
              onClick={() => setSelectedTab(null)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                selectedTab === null
                  ? "border-violet-600 text-violet-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              All Reports ({savedReports.length})
            </button>

            {/* User-created tabs */}
            {tabs.map((tab) => {
              const tabReportCount = savedReports.filter(r => r.tab_id === tab.id).length;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    selectedTab === tab.id
                      ? "border-violet-600 text-violet-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {tab.name} ({tabReportCount})
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-6 pt-4 pb-8">
        {savedReports.length === 0 ? (
          <div className="text-center py-32">
            <h2 className="text-xl text-gray-600 mb-4">
              No Saved Reports
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Get started by using a template or go to the Reporting tab to create a custom report.
            </p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setShowTemplateModal(true)}
                className="px-6 py-3 bg-violet-700 text-white rounded-md text-sm font-medium hover:bg-violet-800 transition-colors duration-200"
              >
                Use a Template
              </button>
              <button
                onClick={() => props.setCurrentTab("reporting")}
                className="px-6 py-3 border border-violet-700 text-violet-700 rounded-md text-sm font-medium hover:bg-violet-50 transition-colors duration-200"
              >
                Go to Reporting
              </button>
            </div>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-32 text-gray-500">
            <p>No reports in this tab.</p>
            <button
              onClick={() => setShowTemplateModal(true)}
              className="mt-4 px-4 py-2 text-sm text-violet-600 hover:bg-violet-50 rounded-md transition-colors duration-200"
            >
              Use a Template
            </button>
          </div>
        ) : (
          <div className="grid xl:grid-cols-1 grid-cols-2 gap-6">
            {filteredReports.map((report, index) => {
              // Render LogbookReportCard for logbook reports
              if (report.report_type === "logbook") {
                return (
                  <div key={report.id || index} className="col-span-2 xl:col-span-1">
                    <LogbookReportCard
                      report={report}
                      homeStore={props.homeStore}
                      storeNames={props.storeNames}
                      businessWeekStartDayIndex={businessWeekStartDayIndex}
                      dateRangeOverride={
                        dateRangeOverride
                          ? {
                              type: dateRangeOverride,
                              startDate: overrideStartDate,
                              endDate: overrideEndDate,
                            }
                          : null
                      }
                    />
                  </div>
                );
              }

              // Render regular ReportCard for location/employee reports
              return (
                <ReportCard
                  key={report.id || index}
                  report={report}
                  storeNames={props.storeNames}
                  availableMetrics={availableMetrics}
                  onRefresh={fetchSavedReports}
                  isLoading={loading}
                  dateRangeOverride={dateRangeOverride}
                  overrideStartDate={overrideStartDate}
                  overrideEndDate={overrideEndDate}
                  businessWeekStartDayIndex={businessWeekStartDayIndex}
                  homeStore={props.homeStore}
                />
              );
            })}
          </div>
        )}
      </div>

      <TemplatePickerModal
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onApplyTemplate={handleApplyTemplate}
        applyingTemplate={applyingTemplate}
        storeNames={props.storeNames}
        selectedTab={selectedTab}
      />
    </div>
  );
}

export default DashboardDisplay;
